<template>
  <div class="trainer-processes">
    <!-- Show status badge when processes array is empty -->
    <div v-if="allDone" class="status-col">
      <span v-if="status?.toLowerCase().includes('complete')" class="status-tag _complete"><div class="status-icon">&#x2713;</div>Complete</span>
      <span v-else-if="status?.toLowerCase().includes('error')" class="status-tag _error"><div class="status-icon">&#x2716;</div>Error</span>
      <span v-else-if="status?.toLowerCase().includes('terminate')" class="status-tag _warning"><div class="status-icon">&#x21;</div>Terminated</span>
      <span v-else-if="status" class="status-tag _running"><div class="running-icon" />
        {{ String(status).charAt(0).toUpperCase() + String(status).slice(1) }}
      </span>
    </div>

    <!-- Show processes list when processes array has items -->
    <div v-else class="processes-list">
      <div v-for="process in processes" :key="process.id" class="process-item">
        <TaskProgress
          :status="process.status_info.status"
          :percent="process.status_info.percent || 0"
          :stageNumber="process.status_info.stage_number"
          :totalStages="process.status_info.total_stages"
          :showStages="false"
          :taskUID="process.task_uid"
          :userName="process.username"
          :parentUID="process.parent_uid"
        />
        <div class="process-type-label">{{ process.process_type }}</div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineProps } from 'vue';
import TaskProgress from '@/components/Apps/TaskProgress.vue';

const props = defineProps({

  processes: {
    type: Array,
    required: true,
    default: () => [],
    /**
     * Process object structure:
     * {
     *   id: string (unique identifier),
     *   name: string (process name/title),
     *   progress: number (0-100, percentage complete),
     *   status: string (running, completed, failed, pending),
     *   startTime: Date|string (when process started),
     *   endTime: Date|string (when process ended),
     *   error: string (error message if failed),
     *   ...other fields as needed
     * }
     */
  },
 
});

const status = computed(() => {
  if (!props.processes || props.processes.length === 0) {
    return "";
  }
  return props.processes[0].status_info?.status || "";
});

const allDone = computed(() => {
  if (!props.processes || props.processes.length === 0) {
    return true;
  }
  return props.processes.every((process) => process.status_info?.in_progress === false);
});
</script>

<style lang="scss" scoped>
.trainer-processes {
  width: 100%;
  overflow: hidden;
}

.processes-list {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 8px;
  justify-content: center;
}

.process-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  flex-shrink: 0;
}

.process-type-label {
  font-size: 11px;
  font-weight: 600;
  text-align: center;
  min-width: 60px;
}

/* Status Badge Styles */
.status-col {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}

.status-tag {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 2px 12px;
  border-radius: 16px;
  font-weight: 600;
  font-size: 12px;
  color: #4a4a4a;
  
  &._complete {
    background: rgba(76, 175, 80, 0.1);
    border: 2px solid #4caf50;
  }
  
  &._error {
    background: rgba(244, 67, 54, 0.1);
    border: 2px solid #f44336;
  }
  
  &._warning {
    background: rgba(255, 193, 7, 0.1);
    border: 2px solid #ffc107;
  }
  
  &._running {
    background: rgba(33, 150, 243, 0.1);
    border: 2px solid #2196f3;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }
}

.status-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.running-icon {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #2196f3;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
